import PropTypes from 'prop-types';

import TableRowAction from 'components/common/TableRowAction';
import CustomAdvanceTable, {
  BULK_ACTION_EDIT,
  BULK_ACTION_DELETE
} from 'components/common/advance-table/CustomAdvanceTable';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import CommentFormModal from './CommentFormModal';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import {
  useDonorComments,
  useDeleteDonorComments,
  useUpdateDonorComment
} from 'hooks/useDonors';

/* -------------------------------------------------------------------------- */
const bulkActionsOptions = [
  {
    label: 'Delete',
    value: BULK_ACTION_DELETE
  }
];
/* -------------------------------------------------------------------------- */

function DonorCommentsTable({ donorId, setShowCommentForm }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);

  const { comments, isLoading: getCommentsLoading } = useDonorComments(donorId);
  const { deleteComments, isDeleting: deleteLoading } = useDeleteDonorComments();
  const { updateComment, isUpdating: editLoading } = useUpdateDonorComment();

  const columns = [
    {
      accessor: 'reporter',
      Header: 'reporter',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { first_name, last_name } = rowData.row.original.user;
        return (
          <p className="text-truncate mb-0" style={{ maxWidth: '400px' }}>
            {first_name} {last_name}
          </p>
        );
      }
    },
    {
      accessor: 'date',
      Header: 'Date',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { created_at } = rowData.row.original;
        const data = format(new Date(created_at), 'dd/MM/yyyy');
        return data;
      }
    },
    {
      accessor: 'description',
      Header: 'Description',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => (
        <p className="text-truncate mb-0" style={{ maxWidth: '400px' }}>
          {rowData.row.original.description}
        </p>
      )
    },
    {
      accessor: 'id',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,

      Cell: rowData => {
        const { id } = rowData.row;
        return (
          <TableRowAction
            onCLickEdit={() => handleEdit(id)}
            onCLickDelete={() => onDelete([id])}
            subject={permissionsKeyMap.donor.children.donors}
          />
        );
      }
    }
  ];

  const handleConfirmDelete = () => {
    deleteComments(selectedIds);
    handleDeleteModalHide();
  };

  const onDelete = ids => {
    setSelectedIds(ids);
    setShowDeleteModal(true);
  };

  const handleBulkAction = (action, ids) => {
    switch (action) {
      case BULK_ACTION_DELETE:
        onDelete(ids);
        break;
      case BULK_ACTION_EDIT:
        break;
      default:
        break;
    }
  };

  const handleDeleteModalHide = () => {
    setShowDeleteModal(false);
    setSelectedIds([]);
  };

  const handleEditModalHide = () => {
    setShowEditModal(false);
    setSelectedIds([]);
  };

  const handleEdit = id => {
    setSelectedIds([id]);
    setShowEditModal(true);
  };

  const handleEditSubmit = data => {
    updateComment({
      id: selectedIds[0],
      data: { description: data.comment }
    });
    setShowEditModal(false);
  };

  const editInitialValues = useMemo(() => {
    if (!selectedIds.length) {
      return {};
    }
    const comment = comments?.find(
      item => item.id === selectedIds[0]
    );
    const { description } = comment ?? { description: '' };
    return {
      comment: description
    };
  }, [selectedIds, comments]);

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal}
        onHide={handleDeleteModalHide}
        loading={deleteLoading}
        title="Delete Donor Comments"
        handleConfirm={handleConfirmDelete}
        body="Are you sure you want to delete selected donor comments?"
      />
      <CommentFormModal
        show={showEditModal}
        onHide={handleEditModalHide}
        onSubmit={handleEditSubmit}
        loading={editLoading}
        initialValues={editInitialValues}
        isEdit
      />
      {comments && (
        <CustomAdvanceTable
          data={comments}
          columns={columns}
          title="Comments"
          showSearchInput
          searchInputPlaceHolder="Search"
          addNew
          onClickAddNew={() => setShowCommentForm(true)}
          perPage={50}
          loading={getCommentsLoading}
          bulkActionsOptions={bulkActionsOptions}
          handleBulkAction={handleBulkAction}
        />
      )}
    </>
  );
}

DonorCommentsTable.propTypes = {
  donorId: PropTypes.string,
  setShowCommentForm: PropTypes.func
};

export default DonorCommentsTable;
